import React from 'react';
import { RiCloseCircleFill, RiSearchLine } from 'react-icons/ri';
import TouchableOpacity from './TouchableOpacity';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { styled } from '@mui/system';
import getThemeLookup from '../selectors/getThemeLookup';
import { CONTAINER_PROPERTIES } from '../utils/theme';
import combineStyles from '../utils/combineStyles';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import { updateForm } from '../slices/deliveryAddressForm';

interface LocationSearchProps {
  placeholder?: string;
  containerStyle?: React.CSSProperties;
  onClick?: () => void;
  showClearButton?: boolean;
  autoFocus?: boolean;
}

const LocationSearch = ({
  placeholder,
  containerStyle,
  onClick,
  showClearButton,
  autoFocus,
}: LocationSearchProps) => {
  const dispatch = useAppDispatch();
  const p = useAppSelector(getThemeLookup);
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);
  const formState = useAppSelector(state => state.deliveryAddressForm);

  const addressInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateForm({ addressInput: e.target.value }));
    if (!formState.editingAddress) {
      dispatch(updateForm({ selectedAddress: undefined }));
    }
  };

  const clearAddressInput = () => {
    dispatch(updateForm({ addressInput: '' }));
    if (!formState.editingAddress) {
      dispatch(updateForm({ selectedAddress: undefined }));
    }
  };

  return (
    <div
      style={{
        ...styles.searchContainer,
        ...(deviceTypeMobile ? styles.searchContainerMobile : styles.searchContainerDesktop),
        ...containerStyle,
      }}
    >
      <SearchWrapper
        style={combineStyles([
          p('searchBar', CONTAINER_PROPERTIES),
          { backgroundColor: p('searchBar', CONTAINER_PROPERTIES).backgroundColor || 'whitesmoke' },
        ])}
        onClick={onClick}
      >
        <TouchableOpacity>
          <RiSearchLine size={20} color={p('searchBarIcon', ['color']).color || 'black'} />
        </TouchableOpacity>

        <input
          className="location-search px-3"
          name="formattedAddress"
          placeholder={placeholder}
          value={formState.addressInput || ''}
          onChange={addressInputChanged}
          style={{ border: 'none', width: '100%', backgroundColor: 'transparent' }}
          autoFocus={autoFocus}
        />

        {showClearButton && (
          <TouchableOpacity onClick={clearAddressInput}>
            <RiCloseCircleFill size={20} color={p('searchBarIcon', ['color']).color || 'black'} />
          </TouchableOpacity>
        )}
      </SearchWrapper>
    </div>
  );
};

const SearchWrapper = styled('div')({
  borderRadius: 30,
  backgroundColor: 'whitesmoke',
  display: 'flex',
  flexDirection: 'row',
  flex: 1,
  height: '2.8em',
  alignItems: 'center',
  padding: '0 1em',
  transition: '0.25s all ease-out',
  outline: '#DFDFDF solid',
  outlineWidth: 0,
  outlineOffset: -1,

  '&:hover': {
    background: '#EBEBEB',
  },

  '&:focus-within': {
    outlineWidth: 1,
    background: '#fff',
  },
});

const styles: Styles = {
  searchContainerMobile: {
    display: 'block',
  },
  searchContainerDesktop: {
    display: 'flex',
    gap: '0.75em',
  },
  switchWrapper: {
    display: 'flex',
  },
  switchWrapperMobile: {
    marginTop: 10,
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: '1em',
    justifyContent: 'space-between',
  },
  switchWrapperDesktop: {
    gap: 0,
    flexDirection: 'column',
  },
};

export default LocationSearch;
